import { useMemo } from 'react';

// Third-party
import { alpha, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';

// Hooks
import { TCustomTheme } from '@mgm/common/theme';
import { useAppSelector } from '../../../../hooks/useAppStore';

// Redux-toolkit
import { getFormattedSubmissions } from '../../../../store/tasklists/selectors';

// Assets
import { ReactComponent as DoneIcon } from '../../../../static/icons/tasklists/ic_done.svg';
import { ReactComponent as SubmissionsIcon } from '../../../../static/icons/tasklists/ic_submissions.svg';
import { ReactComponent as InProgressIcon } from '../../../../static/icons/tasklists/ic_in_progress.svg';

// Types

import { TEmptySubmission } from '../../../../types';

export function useSubmissions() {
  const theme = useTheme() as TCustomTheme;
  const { t } = useTranslation('tasklists');
  const formattedSubmissions = useAppSelector(getFormattedSubmissions);

  const totalData = useMemo(() => {
    const doneCount = formattedSubmissions?.reduce(
      (acc: Array<TEmptySubmission>, submission) => {
        const inProgress = submission.tasks?.some((task) => !task.doneBy.length);

        if (inProgress) {
          acc.push(submission);
        }

        return acc;
      },
      [],
    );

    return {
      doneCount: (formattedSubmissions?.length || 0) - (doneCount?.length || 0),
      inProgress: doneCount?.length,
      totalSubmissions: formattedSubmissions.length || 0,
    };
  }, [formattedSubmissions]);

  const getTotalItems = useMemo(() => [
    {
      title: t('submissions'),
      value: totalData.totalSubmissions,
      icon: SubmissionsIcon,
      cardBg: theme.palette.info.lighter,
      cardColor: theme.palette.info.darker,
      fontSize: 'medium',
      iconBg: `linear-gradient(135deg, ${alpha(theme.palette.info.dark, 0)} 0%, ${alpha(
        theme.palette.info.dark,
        0.24,
      )} 100%)`,
    },
    {
      title: t('done'),
      value: totalData.doneCount,
      icon: DoneIcon,
      fontSize: 'medium',
      cardColor: '#005249',
      cardBg: '#C8FACD',
      iconBg: `linear-gradient(135deg, ${alpha('#007B55', 0)} 0%, ${alpha(
        '#007B55',
        0.24,
      )} 100%)`,
    },
    {
      title: t('inProgress'),
      cardColor: theme.palette.warning.darker,
      cardBg: theme.palette.warning.lighter,
      value: totalData.inProgress,
      fontSize: 'medium',
      icon: InProgressIcon,
      iconBg: `linear-gradient(135deg, ${alpha(theme.palette.warning.dark, 0)} 0%, ${alpha(
        theme.palette.warning.dark,
        0.24,
      )} 100%)`,
    },
  ], [totalData]);

  return {
    getTotalItems,
  };
}
